/**
 * California statewide electricity (kWh) and natural gas (therms) from published community GHG release
 * State rows — same basis as upsert_california_state_ghg_from_counties (sums of CA counties).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { CALIFORNIA_STATE_GEOFIPS } from './californiaGhgInventoryVmtService';
import { fetchCommunityGhgReleaseRows } from './communityGhgInventoryReleaseService';

export interface CaliforniaStateEnergyActivityRow {
  year: number;
  elec_residential_kwh: number;
  elec_commercial_kwh: number;
  /** MT CO₂e from precalc when present (preferred for GHG charts vs recomputing from kWh). */
  elec_residential_emissions: number | null;
  elec_commercial_emissions: number | null;
  ng_residential_therms: number;
  ng_commercial_therms: number;
}

function numNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Paginated fetch of State geography rows for California (geofips 6).
 */
function rowsFromRelease(
  releaseRows: Awaited<ReturnType<typeof fetchCommunityGhgReleaseRows>>['rows']
): CaliforniaStateEnergyActivityRow[] {
  const rows: CaliforniaStateEnergyActivityRow[] = [];
  for (const r of releaseRows) {
    if (!Number.isFinite(r.year)) continue;
    rows.push({
      year: r.year,
      elec_residential_kwh: num(r.elec_residential_kwh),
      elec_commercial_kwh: num(r.elec_commercial_kwh),
      elec_residential_emissions: numNull(r.elec_residential_emissions),
      elec_commercial_emissions: numNull(r.elec_commercial_emissions),
      ng_residential_therms: num(r.ng_residential_therms),
      ng_commercial_therms: num(r.ng_commercial_therms),
    });
  }
  rows.sort((a, a2) => a.year - a2.year);
  return rows;
}

function sumCountyRowsByYear(
  releaseRows: Awaited<ReturnType<typeof fetchCommunityGhgReleaseRows>>['rows']
): CaliforniaStateEnergyActivityRow[] {
  const byYear = new Map<number, CaliforniaStateEnergyActivityRow>();
  for (const r of releaseRows) {
    if (!Number.isFinite(r.year)) continue;
    const year = r.year;
    const cur =
      byYear.get(year) ??
      ({
        year,
        elec_residential_kwh: 0,
        elec_commercial_kwh: 0,
        elec_residential_emissions: null,
        elec_commercial_emissions: null,
        ng_residential_therms: 0,
        ng_commercial_therms: 0,
      } satisfies CaliforniaStateEnergyActivityRow);
    cur.elec_residential_kwh += num(r.elec_residential_kwh);
    cur.elec_commercial_kwh += num(r.elec_commercial_kwh);
    cur.ng_residential_therms += num(r.ng_residential_therms);
    cur.ng_commercial_therms += num(r.ng_commercial_therms);
    const er = numNull(r.elec_residential_emissions);
    const ec = numNull(r.elec_commercial_emissions);
    if (er != null) cur.elec_residential_emissions = (cur.elec_residential_emissions ?? 0) + er;
    if (ec != null) cur.elec_commercial_emissions = (cur.elec_commercial_emissions ?? 0) + ec;
    byYear.set(year, cur);
  }
  return Array.from(byYear.values()).sort((a, b) => a.year - b.year);
}

function stateRowsHaveEnergyActivity(rows: CaliforniaStateEnergyActivityRow[]): boolean {
  return rows.some(
    (r) => r.elec_residential_kwh + r.elec_commercial_kwh + r.ng_residential_therms + r.ng_commercial_therms > 0
  );
}

export async function fetchCaliforniaStateEnergyActivityFromGhgInventory(
  _client: SupabaseClient
): Promise<CaliforniaStateEnergyActivityRow[]> {
  const select =
    'year,elec_residential_kwh,elec_commercial_kwh,elec_residential_emissions,elec_commercial_emissions,ng_residential_therms,ng_commercial_therms';

  const { rows: stateReleaseRows, error: stateError } = await fetchCommunityGhgReleaseRows({
    geographyType: 'State',
    geofipsIn: [CALIFORNIA_STATE_GEOFIPS],
    select,
    yearMin: 2005,
    yearMax: 2024,
  });
  if (!stateError) {
    const stateRows = rowsFromRelease(stateReleaseRows);
    if (stateRowsHaveEnergyActivity(stateRows)) return stateRows;
  }

  const { rows: countyRows, error: countyError } = await fetchCommunityGhgReleaseRows({
    geographyType: 'County',
    select,
    yearMin: 2005,
    yearMax: 2024,
  });
  if (countyError || !countyRows.length) return rowsFromRelease(stateReleaseRows);

  const summed = sumCountyRowsByYear(countyRows);
  return stateRowsHaveEnergyActivity(summed) ? summed : rowsFromRelease(stateReleaseRows);
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function isCaliforniaStateFromPopulationRow(row: {
  geofips?: string | number | null;
  geography_type?: string | null;
}): boolean {
  const g = String(row.geofips ?? '').replace(/^0+/, '') || '0';
  const gt = String(row.geography_type || '').toLowerCase();
  return g === String(CALIFORNIA_STATE_GEOFIPS) && gt === 'state';
}
